import React from 'react';
import storage from 'local-storage-fallback';

import { useCurrentArtistIdOrNull } from '../../../artists';
import { useAuthorized } from './useAuthorized';

export enum BannerState {
  success,
  hide,
}

export function useBanner() {
  const authorized = useAuthorized();
  const currentArtistId = useCurrentArtistIdOrNull();
  const key = getKey(currentArtistId);

  const { value, setValue } = useStorage(key);

  const done = React.useCallback(() => {
    setValue(BannerState.hide);
  }, [setValue]);

  return {
    success: value === BannerState.success,
    // Always hide the banner if user is not authorized to submit the
    // fundraising form
    hide: !authorized || value === BannerState.hide,
    done,
  };
}

function useStorage(key: string) {
  const storedValue = fromString(storage.getItem(key) ?? '');

  const [value, setInnerValue] = React.useState<BannerState>(storedValue);

  React.useEffect(() => {
    // Re-read the value from storage whenever key changed
    setInnerValue(storedValue);
  }, [key, setInnerValue, storedValue]);

  function setValue(newValue: BannerState) {
    setInnerValue(newValue);
    storage.setItem(key, toString(newValue));
  }

  // I've observed that when there's a discrepancy it's because there's a 1
  // render delay where the value in state is not yet synchronized to a value
  // from local storage. E.g. when switching between current artists (changing
  // the key). To prevent a render flash of the wrong banner, use the stored
  // value read directly (synchronously) from storage when there's a
  // synchronization delay.
  return { value: storedValue !== value ? storedValue : value, setValue };
}

export function useShowSuccess() {
  const currentArtistId = useCurrentArtistIdOrNull();
  const key = getKey(currentArtistId);

  return function showSuccess() {
    storage.setItem(key, toString(BannerState.success));
  };
}

export function useHideBanner() {
  const currentArtistId = useCurrentArtistIdOrNull();
  const key = getKey(currentArtistId);

  return function hideBanner() {
    storage.setItem(key, toString(BannerState.hide));
  };
}

export function getKey(artistId: string | null) {
  return `fundraising_banner_${artistId || ''}`;
}

function toString(v: BannerState) {
  return v.toString();
}

function fromString(v: string) {
  const asInt = parseInt(v || '', 10) || 0;
  return clip(asInt);
}

function clip(num: number): BannerState {
  if ([0, 1, 2].includes(num)) return num;
  return 0;
}
